import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import BoardMeetings from "@/pages/governance/BoardMeetings";

const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

export default function VotesApprovals() {
  const [votes, setVotes] = useState([]);
  const [openMeeting, setOpenMeeting] = useState(null);
  const load = useCallback(async () => { const r = await api.get("/board-pending-votes"); setVotes(r.data.votes); }, []);
  useEffect(() => { load(); }, [load]);

  const cast = async (v, choice) => {
    try { await api.post(`/board-meetings/${v.meeting_id}/votes/${v.id}/ballot`, { choice }); toast.success(`Voted ${choice}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not vote"); }
  };
  const openWs = async (mid) => { const r = await api.get(`/board-meetings/${mid}`); setOpenMeeting(r.data); };
  if (openMeeting) return <BoardMeetingsInline initial={openMeeting} onBack={() => { setOpenMeeting(null); load(); }} />;

  return (
    <div data-testid="votes-approvals-page">
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1">Votes &amp; Approvals</h1>
      <p className="text-[16px] text-ink/70 mb-5">Every open ballot across upcoming meetings, in one place.</p>
      <div className="space-y-3">
        {votes.length === 0 && <div className="bg-cream border border-ink/15 rounded-xl p-10 text-center text-ink/55">No open votes right now.</div>}
        {votes.map((v) => (
          <div key={v.meeting_id + v.id} className="bg-cream border border-ink/15 rounded-xl p-4" data-testid={`pending-vote-${v.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-semibold text-[16px]">{v.title}</span>
              <button data-testid={`vote-meeting-link-${v.id}`} onClick={() => openWs(v.meeting_id)} className="text-[13px] text-dark-olive font-semibold underline shrink-0">{fmt(v.meeting_date)} →</button>
            </div>
            {v.description && <p className="text-[14px] text-ink/65 mb-2">{v.description}</p>}
            <div className="text-[13px] text-ink/60 mb-2">Yes {v.tally.yes} · No {v.tally.no} · Abstain {v.tally.abstain}{v.my_choice ? ` · you voted ${v.my_choice}` : ""}</div>
            <div className="flex gap-2 flex-wrap">
              {["yes", "no", "abstain"].map((c) => (
                <button key={c} data-testid={`va-ballot-${v.id}-${c}`} onClick={() => cast(v, c)} className={`rounded-lg px-3 py-2 min-h-[40px] text-[13px] font-semibold capitalize border ${v.my_choice === c ? "bg-ink text-cream border-ink" : "border-ink/25"}`}>{c}{v.my_choice === c ? " ✓" : ""}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BoardMeetingsInline({ initial, onBack }) {
  // Render the full workspace by reusing BoardMeetings' internal Workspace via a light wrapper
  const [BM] = useState(() => BoardMeetings);
  return <div><button onClick={onBack} data-testid="va-back" className="text-[14px] text-dark-olive font-semibold mb-2 flex items-center gap-1"><Icon name="ChevronRight" className="w-4 h-4 rotate-180" /> Back to votes</button><BM key={initial.meeting_id} /></div>;
}
